import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

// Utils
import { CONSTANTS, getExactMatrixDimensions } from "../utils";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { setObjectsList } from "@/Common/helpers/objects";
import { getOEMParameters } from "@/Common/helpers/OEMs";
import { removeListValue } from "@/Common/helpers/Arrays";

// Custom components
import CommissionsMatrix from "./CommissionsMatrix";
import VariableGrossStructure from "./VariableGrossStructure";

// MUI
import { Divider, Grid, Stack } from "@mui/material";

const modifyObject = (object, key, value, applyToAll, applyToAllOpposite, excludeKeys = []) => {
	Object.entries(object).forEach(([objectKey]) => {
		if (!excludeKeys.includes(objectKey)) {
			if (key === objectKey) {
				object[objectKey] = value;
			} else if (applyToAll) {
				object[objectKey] = applyToAllOpposite ? !value : value;
			}
		}
	});
	return object;
};

const getFinanceTabOptions = (variableGrossStructure, tab) => {
	const showNewFinanceGrossMatrix = variableGrossStructure?.newFinance && tab?.combineOEMs;
	const showPreOwnedFinanceMatrix = variableGrossStructure?.preOwnedFinance;
	const showOEMMatrices = variableGrossStructure?.newFinance && !tab?.combineOEMs;
	const showCombinedGrossStructure = variableGrossStructure?.newFinance;

	return {
		showNewFinanceGrossMatrix,
		showOEMMatrices,
		showPreOwnedFinanceMatrix,
		showCombinedGrossStructure,
	};
};

const getTitleForMatrix = (make, oems, objectRef) => {
	if (objectRef === "finance") {
		const makeName = String(make?.make)?.toUpperCase();
		if (makeName === "GLOBAL") {
			return "Total F&I Gross";
		}
		if (makeName.includes("FINANCE")) {
			if (makeName.includes("PREOWNED")) {
				return "Total Pre-Owned F&I Gross";
			} else if (makeName.includes("NEW")) {
				return "Total New F&I Gross";
			}
		} else if (oems?.some((oem) => String(oem?.oemReference).toUpperCase() === makeName)) {
			const currentOEM = getOEMParameters({ oemReference: make?.make, dealerCodeFS: make?.dealerCodeFS, locationOEMs: oems });
			const displayName = currentOEM?.oemName || make?.make;
			return `Total New ${displayName} F&I Gross`;
		}
	}
	return "Total Variable Gross";
};

const getReadOnlyStatus = (args) => {
	const { readOnly, objectRef, make, editStatus } = args;
	const isFinance = objectRef === "finance";

	if (readOnly) {
		return true;
	}
	if (!isFinance || make?.make === "newFinance" || make?.make === "preOwnedFinance") {
		return !editStatus?.makes && make?.make !== "preOwnedFinance";
	}
	if (isFinance && make?.make === "global") {
		return false;
	}
	if (editStatus?.["oemMatrices"]?.length) {
		return !editStatus?.["oemMatrices"].some((matrixKey) => {
			if (matrixKey === "global") {
				return editStatus?.["oemMatrices"].includes("global");
			} else {
				const matchesMake = String(matrixKey?.make).toUpperCase() === String(make?.make).toUpperCase();
				return matchesMake && matrixKey?.dealerCodeFS === make?.dealerCodeFS;
			}
		});
	}
	return true;
};

const isTierDollarPerUnit = (tiers) =>
	tiers?.some?.((tier) => tier?.commissions?.some((commission) => commission?.commission?.input === "Dollar"));

export default function CommissionSection(props) {
	const {
		makes = [],
		readOnly = false,
		combinedOEMsGrossStructure = null,
		variableGrossStructure = null,
		newAndPreOwnedRequired = false,
		financeGlobalUnitCounts,
		franchises = [],
		oems = [],
		tabsErrors = [],
		objectRef = "",
		tab = null,
		backendProductsCount = 7,
		onActionUpdate = () => {},
		onActionValidation = () => {},
		onCombinedGrossStructureUpdate = () => {},
		onVariableGrossStructureUpdate = () => {},
		validateAgainst0,
	} = props;

	const isFinance = objectRef === "finance";

	// Control to send update actions
	const wasUpdated = useRef(false);
	const saveErrors = useRef([]);

	const minimumColumnsInput = 0;
	const maximumColumnsInput = isFinance ? backendProductsCount : CONSTANTS.MAX_COLUMNS_INPUT;

	// Unique OEM's
	const uniqueOEMs = setObjectsList(oems, ["oemReference"]);

	// States
	const [makesInTab, setMakesInTab] = useState([]);
	const [variableGrossStructureInTab, setVariableGrossStructureInTab] = useState(null);
	const [combinedOEMsGrossStructureInTab, setCombinedOEMsGrossStructureInTab] = useState(null);

	const [errorsMap, setErrorsMap] = useState(new Map());
	const [editStatus, setEditStatus] = useState(null);

	const [financeTabOptions, setFinanceTabOptions] = useState(null);

	const resizeMakes = (makes, matrixDimensions, newMatrixDimensions, makesDoNotModify = []) => {
		const unalteredMakes = makes.filter((element) => makesDoNotModify.includes(element?.make));
		makes = makes?.filter((element) => !makesDoNotModify.includes(element?.make));
		const tiersDifference =
			newMatrixDimensions.maxTiers - matrixDimensions.maxTiers || newMatrixDimensions.minTiers - matrixDimensions.minTiers;
		const columnsDifference =
			newMatrixDimensions.maxColumns - matrixDimensions.maxColumns || newMatrixDimensions.minColumns - matrixDimensions.minColumns;
		if (columnsDifference !== 0 || tiersDifference !== 0) {
			if (tiersDifference >= 0 && columnsDifference >= 0) {
				// tiers got bigger
				// columns got bigger
				makes = expandMakes(makes, newMatrixDimensions.maxTiers, newMatrixDimensions.maxColumns);
			} else if (tiersDifference <= 0 && columnsDifference <= 0) {
				// tiers got smaller
				// columns got smaller
				makes = shrinkMakes(makes, newMatrixDimensions.minTiers, newMatrixDimensions.minColumns, true);
			} else if (tiersDifference >= 0 && columnsDifference <= 0) {
				// tiers got bigger
				// columns got smaller
				makes = expandMakes(makes, newMatrixDimensions.maxTiers, matrixDimensions.maxColumns);
				makes = shrinkMakes(makes, newMatrixDimensions.maxTiers, newMatrixDimensions.minColumns);
			} else if (tiersDifference <= 0 && columnsDifference >= 0) {
				// tiers got smaller
				// columns got bigger
				makes = shrinkMakes(makes, newMatrixDimensions.minTiers, matrixDimensions.maxColumns);
				makes = expandMakes(makes, newMatrixDimensions.minTiers, newMatrixDimensions.maxColumns);
			}
		}
		return [...clone(makes), ...unalteredMakes];
	};

	const shrinkMakes = (updatedMakes = [], maxTiers = 1, maxColumns = 1, clearError = false) => {
		const shrunk = clone(updatedMakes);
		for (const make of shrunk) {
			// Reducing rows tiers
			if (make.tiers.length !== maxTiers && clearError) {
				saveErrors.current = removeListValue(saveErrors.current, make?.make);
			}

			make.tiers.length = maxTiers;
			while (!make.tiers[maxTiers - 1]) {
				maxTiers--;
			}

			make.tiers[maxTiers - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;

			for (const tiers of make?.tiers || []) {
				const commissionTiers = tiers?.commissions;
				if (commissionTiers?.length !== maxColumns && clearError) {
					saveErrors.current = removeListValue(saveErrors.current, make?.make);
				}

				// Reducing columns tiers
				commissionTiers.length = maxColumns;
				commissionTiers[maxColumns - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;
			}
		}

		return shrunk;
	};

	const expandMakes = (makesToExpand = [], maxTiers = 1, maxColumns = 1) => {
		const newErrors = [];
		for (const make of makesToExpand) {
			if (make?.tiers?.length < maxTiers) {
				newErrors.push(make?.make);
				make.tiers[make.tiers.length - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;
				while (make?.tiers?.length < maxTiers) {
					const newTier = clone(make.tiers[make.tiers.length - 1]);
					newTier.from.value = CONSTANTS.MAX_GROSS_INPUT + 0.01;
					newTier.commissions.forEach((commission) => {
						commission.commission.value = 0;
					});
					make?.tiers?.push(newTier);
				}
				make.tiers[make.tiers.length - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;
			}

			for (const columnTiers of make?.tiers || []) {
				const tierCommissions = columnTiers?.commissions;
				if (tierCommissions?.length < maxColumns) {
					newErrors.push(make?.make);
					tierCommissions[tierCommissions.length - 1].to.value = minimumColumnsInput;

					const newTierCommission = clone(tierCommissions[tierCommissions.length - 1]);
					newTierCommission.from.value = minimumColumnsInput;
					newTierCommission.commission.value = 0;
					while (tierCommissions?.length < maxColumns) {
						tierCommissions.push(clone(newTierCommission));
					}
				}
				tierCommissions[tierCommissions.length - 1].to.value = maximumColumnsInput;
			}
		}
		if (newErrors.length) {
			saveErrors.current = saveErrors?.current?.length ? [...saveErrors.current, ...newErrors] : newErrors;
		}

		return makesToExpand;
	};

	const getTabValidationState = (errorsMap) => {
		let valid = !saveErrors?.current?.length;
		if (valid) {
			for (let [key, value] of errorsMap) {
				let normalizedKey = String(key).toLowerCase()?.trim();
				if (
					normalizedKey !== "variablegrossstructure" &&
					normalizedKey !== "combinedoemsgrossstructure" &&
					!franchises.some((franchise) => String(franchise)?.toLowerCase())
				) {
					errorsMap.set(key, true);
				} else if (!value) {
					valid = false;
				}
			}
		}
		return valid;
	};

	const getEnabledStatuses = (errorsMap, makes, variableGrossStructure, combinedGrossStructure, franchises, readOnly = false) => {
		const hasMakes = Boolean(makes?.length);
		const hasCombinedGrossStructure = isFinance && Boolean(combinedGrossStructure);
		const hasVariableGrossStructure = isFinance ? Boolean(variableGrossStructure) : Boolean(variableGrossStructure?.length);
		let hasMakesError = false;
		const hasCombinedGrossStructureError = hasCombinedGrossStructure ? !errorsMap.get("combinedOEMsGrossStructure") : false;
		const hasVariableGrossStructureError = hasVariableGrossStructure ? !errorsMap.get("variableGrossStructure") : false;

		let allowEdit = {
			variableGrossStructure: true,
			combinedOEMsGrossStructure: true,
			makes: true,
		};

		const exclude = ["oemMatrices"];

		const OEMsAllowedToEdit = [];
		if (hasCombinedGrossStructure) {
			makes.forEach((make) => {
				let makeTitle = String(make?.make);
				let corespondingOemAllowedToEdit = combinedGrossStructure?.find(
					(oem) => oem?.make === makeTitle && oem?.checked && oem?.dealerCodeFS === make?.dealerCodeFS
				);
				if (corespondingOemAllowedToEdit) {
					OEMsAllowedToEdit.push({
						make: makeTitle,
						dealerCodeFS: corespondingOemAllowedToEdit?.dealerCodeFS,
					});
				}
			});
			if (financeGlobalUnitCounts && !hasCombinedGrossStructureError && !hasVariableGrossStructureError) {
				OEMsAllowedToEdit.push("global");
			}
			if (makes?.[0]?.tiers?.[0]?.commissions?.[0]?.commission?.input === "Dollar") {
				allowEdit["combinedOEMsGrossStructure"] = false;
				exclude.push("combinedOEMsGrossStructure");
			}
			allowEdit["oemMatrices"] = OEMsAllowedToEdit;
		}

		if (readOnly) {
			return allowEdit;
		}

		if (isFinance) {
			let isInvalid = false;
			// eslint-disable-next-line no-unused-vars
			for (const [key, value] of errorsMap.entries()) {
				if (!value) {
					isInvalid = true;
				}
			}
			if (!isInvalid) {
				return modifyObject(allowEdit, "", true, true, false, exclude);
			}
		}

		if (hasMakes) {
			for (let [key, value] of errorsMap.entries()) {
				if (key !== "variableGrossStructure" && key !== "combinedOEMsGrossStructure" && !value) {
					hasMakesError = true;
				}
			}
		}

		if (!hasCombinedGrossStructureError && !hasMakesError && !hasVariableGrossStructureError) {
			if (isFinance && hasCombinedGrossStructure) {
				allowEdit["oemMatrices"] = OEMsAllowedToEdit;
				return allowEdit;
			} else {
				return modifyObject(allowEdit, "", true, true, false, exclude);
			}
		}

		if (hasCombinedGrossStructure) {
			if (hasCombinedGrossStructureError) {
				allowEdit = modifyObject(allowEdit, "combinedOEMsGrossStructure", true, true, true, exclude);
			} else {
				if (hasVariableGrossStructure) {
					if (hasVariableGrossStructureError) {
						allowEdit = modifyObject(allowEdit, "variableGrossStructure", true, true, true);
					} else {
						if (hasMakes) {
							if (hasMakesError) {
								allowEdit = modifyObject(allowEdit, "makes", true, true, true, exclude);
							} else {
								allowEdit = modifyObject(allowEdit, "", true, true, false, exclude);
							}
						}
					}
				} else {
					if (hasMakes) {
						if (hasMakesError) {
							allowEdit = modifyObject(allowEdit, "makes", true, true, true, exclude);
						} else {
							allowEdit = modifyObject(allowEdit, "", true, true, false, exclude);
						}
					}
				}
			}
		} else {
			if (hasVariableGrossStructure) {
				if (hasVariableGrossStructureError) {
					allowEdit["variableGrossStructure"] = true;
					allowEdit["makes"] = false;
				} else {
					if (hasMakes) {
						if (hasMakesError) {
							allowEdit["variableGrossStructure"] = false;
							allowEdit["makes"] = true;
						} else {
							allowEdit["variableGrossStructure"] = true;
							allowEdit["makes"] = true;
						}
					}
				}
			} else if (hasMakes) {
				allowEdit["makes"] = true;
			}
		}

		if (hasCombinedGrossStructure && !hasCombinedGrossStructureError) {
			allowEdit["oemMatrices"] = OEMsAllowedToEdit;
		}
		return allowEdit;
	};

	const updateMakes = (updatedMakes) => {
		const preventUpdate = Boolean(!wasUpdated?.current || saveErrors?.current?.length);
		if (preventUpdate) {
			onActionValidation?.("", false);
			setMakesInTab(updatedMakes);
		} else {
			const tabIsValid = getTabValidationState(errorsMap);
			wasUpdated.current = true;
			setEditStatus(
				getEnabledStatuses(
					errorsMap,
					updatedMakes,
					variableGrossStructureInTab,
					combinedOEMsGrossStructureInTab,
					franchises,
					readOnly
				)
			);
			onActionValidation?.("", tabIsValid);
			onActionUpdate?.(updatedMakes, tabIsValid);
		}
	};

	const updateVariableGrossStructure = (updatedVariableGrossStructure) => {
		const preventUpdate = Boolean(!wasUpdated?.current || saveErrors?.current?.length);
		if (preventUpdate) {
			onActionValidation?.("", false);
			setVariableGrossStructureInTab(updatedVariableGrossStructure);
		} else {
			const tabIsValid = getTabValidationState(errorsMap);
			wasUpdated.current = true;
			setEditStatus(
				getEnabledStatuses(
					errorsMap,
					makesInTab,
					updatedVariableGrossStructure,
					combinedOEMsGrossStructureInTab,
					franchises,
					readOnly
				)
			);
			onActionValidation?.("", tabIsValid);
			onVariableGrossStructureUpdate(clone(updatedVariableGrossStructure), tabIsValid);
		}
	};

	const updateCombinedGrossStructure = (combinedGrossStructure) => {
		const preventUpdate = Boolean(!wasUpdated?.current || saveErrors?.current?.length);
		if (preventUpdate) {
			onActionValidation?.("", false);
			setCombinedOEMsGrossStructureInTab(combinedGrossStructure);
		} else {
			const tabIsValid = getTabValidationState(errorsMap);
			wasUpdated.current = true;
			setEditStatus(
				getEnabledStatuses(errorsMap, makesInTab, variableGrossStructureInTab, combinedGrossStructure, franchises, readOnly)
			);
			onActionValidation?.("", tabIsValid);
			onCombinedGrossStructureUpdate(clone(combinedGrossStructure), tabIsValid);
		}
	};

	const checkVariableGrossStructureInTab = (tabKey) => {
		if (tabKey === "newVehicles" && Array.isArray(variableGrossStructureInTab)) {
			return !variableGrossStructureInTab?.some((checkBox) => checkBox?.checked);
		}
		return false;
	};

	const handleVariableGrossStructureActionUpdate = (make, updatedVariableGrossStructure, isValid) => {
		wasUpdated.current = true;
		errorsMap.set(make, isValid);
		if (isValid) {
			saveErrors.current = removeListValue(saveErrors.current, make);
		}
		updateVariableGrossStructure(updatedVariableGrossStructure);
	};

	useEffect(() => {
		const defaultErrorsMap = new Map();
		makes?.forEach((make) => {
			if (make?.dealerCodeFS) {
				defaultErrorsMap.set(`${make?.make}-${make?.dealerCodeFS}`, false);
			} else {
				defaultErrorsMap.set(make?.make || make?.makes?.[0]?.make, false);
			}
			defaultErrorsMap.set(make?.make || make?.makes?.[0]?.make, false);
		});

		const defaultMakesInTab = clone(makes);
		const defaultVariableGrossStructureInTab = clone(variableGrossStructure) || null;
		const defaultCombinedOEMsGrossStructureInTab = clone(combinedOEMsGrossStructure) || null;

		if (variableGrossStructureInTab) {
			defaultErrorsMap.set("variableGrossStructure", false);
		}
		if (defaultCombinedOEMsGrossStructureInTab?.length) {
			defaultErrorsMap.set("combinedOEMsGrossStructure", false);
		}

		(saveErrors?.current || [])?.forEach((error) => {
			defaultErrorsMap.set(error, false);
		});

		setErrorsMap(defaultErrorsMap);
		setMakesInTab(defaultMakesInTab);
		setVariableGrossStructureInTab(defaultVariableGrossStructureInTab);
		setCombinedOEMsGrossStructureInTab(defaultCombinedOEMsGrossStructureInTab);
		setFinanceTabOptions(isFinance ? getFinanceTabOptions(variableGrossStructure, tab) : null);

		setEditStatus(
			getEnabledStatuses(
				defaultErrorsMap,
				defaultMakesInTab,
				defaultVariableGrossStructureInTab,
				defaultCombinedOEMsGrossStructureInTab,
				franchises,
				readOnly
			)
		);

		return () => {
			setMakesInTab([]);
			setErrorsMap(new Map());
			setVariableGrossStructureInTab(null);
			setCombinedOEMsGrossStructureInTab(null);
			setFinanceTabOptions(null);
			wasUpdated.current = false;
			saveErrors.current = [];
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [makes, variableGrossStructure, combinedOEMsGrossStructure, tab]);

	useEffect(() => {
		const defaultErrorsMap = new Map();
		if (variableGrossStructureInTab) {
			defaultErrorsMap.set(
				"variableGrossStructure",
				defaultErrorsMap.has("variableGrossStructure") ? defaultErrorsMap.get("variableGrossStructure") : false
			);
		}
		if (combinedOEMsGrossStructureInTab?.length) {
			defaultErrorsMap.set(
				"combinedOEMsGrossStructure",
				defaultErrorsMap.has("combinedOEMsGrossStructure") ? defaultErrorsMap.get("combinedOEMsGrossStructure") : false
			);
		}
		makesInTab.forEach((make) => {
			if (make?.dealerCodeFS) {
				defaultErrorsMap.set(`${make?.make}-${make?.dealerCodeFS}`, false);
			} else {
				defaultErrorsMap.set(make?.make, false);
			}
		});
		(saveErrors?.current || [])?.forEach((error) => {
			defaultErrorsMap.set(error, false);
		});
		setErrorsMap(defaultErrorsMap);
	}, [makesInTab, variableGrossStructureInTab, combinedOEMsGrossStructureInTab]);

	useEffect(() => {
		const enabledStatus = getEnabledStatuses(
			errorsMap,
			makesInTab,
			variableGrossStructureInTab,
			combinedOEMsGrossStructureInTab,
			franchises,
			readOnly
		);
		setEditStatus(enabledStatus);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorsMap]);

	const applyTiersRetroactivelyException = (key) => {
		if (isFinance && isTierDollarPerUnit(makesInTab?.[key]?.tiers)) {
			makesInTab[key].applyTiersRetroactively = true;
		}
	};

	return (
		<>
			{makesInTab?.map((make, key) => {
				const compareNumber =
					!financeGlobalUnitCounts && financeTabOptions?.showPreOwnedFinanceMatrix
						? makesInTab?.length - 2
						: makesInTab?.length - 1;
				const isVariableGross = financeTabOptions?.showCombinedGrossStructure && key === compareNumber;
				const marginTop = key > 0 ? 4 : 0;
				const validateAgainstNoCommission = variableGrossStructureInTab?.some?.((checkBox) => checkBox?.checked)
					? true
					: validateAgainst0;
				return (
					<Grid key={`${make?.make}_${key}`} container item xs={12} mt={marginTop}>
						{financeTabOptions?.showPreOwnedFinanceMatrix && key === makesInTab?.length - 1 && makesInTab?.length > 1 && (
							<Stack width={"100%"}>
								<Divider
									className="!border-disabled"
									sx={{
										paddingTop: "36px",
									}}
								/>
							</Stack>
						)}
						<CommissionsMatrix
							make={make?.make}
							dealerCodeFS={make?.dealerCodeFS}
							tiers={make?.tiers}
							tabsErrors={tabsErrors}
							onActionValidation={(updatedMake, dealerCodeFS, isValid) => {
								if (dealerCodeFS) {
									errorsMap.set(`${updatedMake}-${dealerCodeFS}`, isValid);
								} else {
									errorsMap.set(updatedMake, isValid);
								}
								const tabValidState = getTabValidationState(errorsMap);
								onActionValidation?.(makes, tabValidState);
							}}
							onActionUpdate={(updatedMake, dealerCodeFS, updatedTiers, isValid, dimensionsForMatrix) => {
								applyTiersRetroactivelyException(key);
								wasUpdated.current = true;
								if (dealerCodeFS) {
									errorsMap.set(`${updatedMake}-${dealerCodeFS}`, isValid);
								} else {
									errorsMap.set(updatedMake, isValid);
								}
								if (isValid) {
									saveErrors.current = removeListValue(saveErrors.current, updatedMake);
								}
								makesInTab[key].tiers = updatedTiers;

								if (Boolean(newAndPreOwnedRequired) && isValid) {
									makesInTab.forEach((item, index) => {
										const dimensions = getExactMatrixDimensions(item?.tiers);
										if (!isSimilarObject(dimensions, dimensionsForMatrix)) {
											item = resizeMakes(
												[item],
												{
													maxTiers: dimensions?.tiers,
													minTiers: dimensions?.tiers,
													maxColumns: dimensions?.columns,
													minColumns: dimensions?.columns,
												},
												{
													maxTiers: dimensionsForMatrix?.tiers,
													minTiers: dimensionsForMatrix?.tiers,
													maxColumns: dimensionsForMatrix?.columns,
													minColumns: dimensionsForMatrix?.columns,
												}
											)[0];
											makesInTab[index] = clone(item);
										}
									});
								}
								updateMakes(clone(makesInTab));
							}}
							title={make?.title || getTitleForMatrix(make, uniqueOEMs, objectRef)}
							readOnly={
								getReadOnlyStatus({ readOnly, objectRef, make, editStatus }) || checkVariableGrossStructureInTab(objectRef)
							}
							showApplyTiersRetroactivelySwitch={isFinance}
							showChargebacksSwitch={isFinance}
							applyTiersRetroactively={make?.applyTiersRetroactively}
							afterChargebacks={make?.afterChargebacks}
							toggleMatrixSwitch={(switchKey, value) => {
								wasUpdated.current = true;
								makesInTab[key][switchKey] = value;
								updateMakes(clone(makesInTab));
							}}
							finance={isFinance}
							backendProductsCount={backendProductsCount + 0.01}
							validateAgainstNoCommission={validateAgainstNoCommission}
						/>

						{isVariableGross && (
							<Grid container item mt={2} xs={12}>
								<VariableGrossStructure
									variableGrossStructure={combinedOEMsGrossStructureInTab}
									franchises={franchises}
									oems={oems}
									oemBased={true}
									showDivider={false}
									showTitle={false}
									readOnly={readOnly}
									onActionValidation={(_make, isValid) => {
										errorsMap.set("combinedOEMsGrossStructure", isValid);
										const tabValidState = getTabValidationState(errorsMap);
										onActionValidation?.(makes, tabValidState);
									}}
									onActionUpdate={(updatedMake, updatedVariableGrossStructure, isValid) => {
										wasUpdated.current = true;
										errorsMap.set("combinedOEMsGrossStructure", isValid);
										if (isValid) {
											saveErrors.current = removeListValue(saveErrors.current, updatedMake);
										}
										updateCombinedGrossStructure(updatedVariableGrossStructure);
									}}
									objectRef={"newVehicles"}
								/>
							</Grid>
						)}
					</Grid>
				);
			})}
			{variableGrossStructureInTab && (
				<Grid container item mt={2} xs={12}>
					<VariableGrossStructure
						tabsErrors={tabsErrors}
						variableGrossStructure={variableGrossStructureInTab}
						franchises={franchises}
						readOnly={readOnly}
						onActionValidation={(updatedMake, isValid) => {
							errorsMap.set(updatedMake, isValid);
							const tabValidState = getTabValidationState(errorsMap);
							onActionValidation?.(makes, tabValidState);
						}}
						onActionUpdate={(make, updatedVariableGrossStructure, isValid) =>
							handleVariableGrossStructureActionUpdate(make, updatedVariableGrossStructure, isValid)
						}
						objectRef={objectRef}
						showDivider={true}
						validateAgainst0={validateAgainst0}
					/>
				</Grid>
			)}
		</>
	);
}
CommissionSection.propTypes = {
	newAndPreOwnedRequired: PropTypes.bool,
	combinedOEMsGrossStructure: PropTypes.array,
	variableGrossStructure: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	tab: PropTypes.object,
	franchises: PropTypes.array,
	oems: PropTypes.array,
	tabsErrors: PropTypes.array,
	objectRef: PropTypes.string,
	readOnly: PropTypes.bool,
	makes: PropTypes.array,
	backendProductsCount: PropTypes.number,
	financeGlobalUnitCounts: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
	onCombinedGrossStructureUpdate: PropTypes.func,
	onVariableGrossStructureUpdate: PropTypes.func,
};
